<script lang="ts">
import BadgeState from '@components/BadgeState/BadgeState.vue';
import { RouteLocationRaw, useRouter } from 'vue-router';
import Title from '@shell/components/Resource/Detail/TitleBar/Title.vue';
import Top from '@shell/components/Resource/Detail/TitleBar/Top.vue';
import ActionMenu from '@shell/components/ActionMenuShell.vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import RcButton from '@components/RcButton/RcButton.vue';
import TabTitle from '@shell/components/TabTitle';
import { computed, ref, VueElement, watch } from 'vue';
import { _CONFIG, AS } from '@shell/config/query-params';
import { ExtensionPoint, PanelLocation } from '@shell/core/types';
import ExtensionPanel from '@shell/components/ExtensionPanel.vue';
import { ButtonVariantNewProps, ButtonSizeNewProps } from '~/pkg/rancher-components/src/components/RcButton/types';
import { isArray } from 'lodash';

export interface Badge {
  color: 'bg-success' | 'bg-error' | 'bg-warning' | 'bg-info';
  label: string;
}

export interface AdditionalActionButton extends ButtonVariantNewProps, ButtonSizeNewProps {
  label: string;
  onClick: () => void;
}

export interface TitleBarProps {
  resource: any;
  resourceTypeLabel: string;
  resourceName: string;

  resourceTo?: RouteLocationRaw;
  description?: string;
  badge?: Badge;

  additionalActions?: VueElement | AdditionalActionButton[];

  // This should be replaced with a list of menu items we want to render.
  // I don't have the time right now to swap this out though.
  actionMenuResource?: any;
  onShowConfiguration?: (returnFocusSelector: string) => void;
}
</script>

<script setup lang="ts">
const {
  additionalActions, resource, resourceTypeLabel, resourceTo, resourceName, description, badge, onShowConfiguration,
} = defineProps<TitleBarProps>();

const store = useStore();
const i18n = useI18n(store);
const router = useRouter();

const emit = defineEmits(['show-configuration']);
const showConfigurationDataTestId = 'show-configuration-cta';
const showConfigurationReturnFocusSelector = computed(() => `[data-testid="${ showConfigurationDataTestId }"]`);

const currentView = ref(router?.currentRoute?.value?.query?.as || _CONFIG);

watch(
  () => currentView.value,
  () => {
    router.push({ query: { [AS]: currentView.value } });
  }
);

const showAdditionalActionButtons = computed(() => isArray(additionalActions));
</script>

<template>
  <div class="title-bar">
    <Top>
      <div class="title-row">
        <Title class="title">
          <TabTitle :show-child="false">
            {{ resourceTypeLabel }}
          </TabTitle>
          <router-link
            v-if="resourceTo"
            :to="resourceTo"
            class="resource-link"
          >
            {{ resourceTypeLabel }}:
          </router-link>
          <span
            v-else
            class="resource-text"
          >
            {{ resourceTypeLabel }}:
          </span>
          <span class="resource-name masthead-resource-title">
            {{ resourceName }}
          </span>
          <BadgeState
            v-if="badge"
            v-ui-context="{ store: store, icon: 'icon-folder', hookable: true, value: resource, tag: '__details-state', description: 'Details' }"
            class="badge-state"
            :color="badge.color"
            :label="badge.label"
          />
        </Title>
        <slot name="title-suffix" />
      </div>
      <div class="actions">
        <slot name="additional-actions">
          <template v-if="additionalActions">
            <template v-if="showAdditionalActionButtons">
              <RcButton
                v-for="(actionButtonProps, i) in (additionalActions as AdditionalActionButton[])"
                :key="`action-button-${i}`"
                :variant="actionButtonProps.variant"
                :size="actionButtonProps.size"
                @click="actionButtonProps.onClick"
              >
                {{ actionButtonProps.label }}
              </RcButton>
            </template>
            <component
              :is="additionalActions"
              v-else
            />
          </template>
        </slot>
        <RcButton
          v-if="onShowConfiguration"
          :data-testid="showConfigurationDataTestId"
          class="show-configuration"
          variant="primary"
          size="large"
          :aria-label="i18n.t('component.resource.detail.titleBar.ariaLabel.showConfiguration', { resource: resourceName })"
          @click="() => emit('show-configuration', showConfigurationReturnFocusSelector)"
        >
          <i
            class="icon icon-document"
            aria-hidden="true"
          />
          {{ i18n.t('component.resource.detail.titleBar.showConfiguration') }}
        </RcButton>
        <ActionMenu
          v-if="actionMenuResource"
          button-variant="multiAction"
          :resource="actionMenuResource"
          data-testid="masthead-action-menu"
          :button-aria-label="i18n.t('component.resource.detail.titleBar.ariaLabel.actionMenu', { resource: resourceName })"
        />
      </div>
    </Top>
    <div
      v-if="description"
      class="bottom description text-deemphasized"
    >
      {{ description }}
    </div>
    <ExtensionPanel
      :resource="resource"
      :type="ExtensionPoint.PANEL"
      :location="PanelLocation.DETAILS_MASTHEAD"
    />
  </div>
</template>

<style lang="scss" scoped>
.title-bar {
  min-width: $resource-detail-min-width;

  .badge-state {
    font-size: 16px;
    margin-left: 12px;
    position: relative;
    flex: 0 0 auto;
  }

  .actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    margin-left: 16px;
  }

  .show-configuration, &:deep() .actions > button {
    margin-left: 16px;
  }

  &:deep() button[data-testid="masthead-action-menu"] {
    border-radius: 4px;
    width: 35px;
    height: 40px;
    margin-left: 16px;

    display: inline-flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .description {
    max-width: 60%;
  }

  // The row holds the heading and whatever a page hangs beside it — an `h1` takes phrasing content
  // only, and a screen reader reads whatever is inside it as part of the heading. It carries the
  // heading's bottom margin too, which on the heading would count towards the row's height.
  .title-row {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    margin-bottom: 10px;
  }

  .title {
    min-width: 0;
    margin-bottom: 0;
  }

  .resource-name {
    display: inline-block;
    flex: 0 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow-x: hidden;
    overflow-y: clip;
    text-overflow: ellipsis;
    margin-left: 4px;
  }
}
</style>
